import { GENERATION_START_MESSAGE } from "views/v2/views/files/finalizeFlowCopy";

/**
 * Maps a `record-generation-progress` (or file-ready) payload to UI progress %.
 */
export function mapRecordGenerationProgressPercent(payload) {
  const progress = payload?.progress || {};
  if (progress.percentage != null && !Number.isNaN(Number(progress.percentage))) {
    return Math.min(100, Math.max(0, Math.round(Number(progress.percentage))));
  }

  const step = String(payload?.step || "").toUpperCase();
  if (step === "COMPLETED") {
    return 100;
  }
  if (step === "STARTED") {
    return 0;
  }
  if (step === "PROGRESS") {
    const current = Number(progress.current) || 0;
    const total = Math.max(Number(progress.total) || 1, 1);
    return Math.min(100, Math.round((current / total) * 100));
  }

  return 0;
}

export function mapRecordGenerationProgressMessage(payload) {
  return payload?.message || "";
}

const PROGRESS_TICK_MS = 300;
const PROGRESS_STEP = 1;
export const PROGRESS_IDLE_BUMP_MS = 2500;
const IDLE_BUMP_PERCENT = 1;
const IDLE_BUMP_MAX = 95;
const STALE_EVENT_MS = 20000;

function getWaitingMessage(elapsedMs) {
  if (elapsedMs >= 5 * 60 * 1000) {
    return "Generation is taking longer than expected. No new status has arrived; please keep this window open while we check for completion.";
  }
  if (elapsedMs >= 2 * 60 * 1000) {
    return "No new generation status has arrived. Larger requests may take several minutes; we are still checking for completion.";
  }
  return "No new generation status has arrived yet. We’ll update this message when one arrives.";
}

/**
 * Animates UI progress from 0 → 100 in 1% steps toward Pusher/backend targets.
 */
export function createSmoothedGenerationProgress(onProgress) {
  let displayed = 0;
  let target = 0;
  let message = "";
  let timer = null;
  let startedAt = 0;
  let lastBackendUpdateAt = 0;

  const emit = (extra = {}) => {
    onProgress?.({
      step: displayed >= 100 ? "COMPLETED" : "PROGRESS",
      message,
      progress: {
        percentage: displayed,
        current: displayed,
        total: 100,
      },
      ...extra,
    });
  };

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const tick = () => {
    if (displayed < target) {
      displayed = Math.min(target, displayed + PROGRESS_STEP);
      emit();
    }
  };

  const ensureTimer = () => {
    if (timer) {
      return;
    }
    timer = setInterval(tick, PROGRESS_TICK_MS);
  };

  const waitUntilDisplayed = (value, timeoutMs = 120000) =>
    new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        if (displayed >= value) {
          resolve();
          return;
        }
        if (Date.now() - started > timeoutMs) {
          displayed = value;
          emit();
          resolve();
          return;
        }
        setTimeout(check, PROGRESS_TICK_MS);
      };
      check();
    });

  return {
    start(initialMessage = GENERATION_START_MESSAGE) {
      displayed = 0;
      target = 0;
      message = initialMessage;
      startedAt = Date.now();
      lastBackendUpdateAt = startedAt;
      emit();
      ensureTimer();
    },
    push(payload = {}) {
      const mapped = mapRecordGenerationProgressPercent(payload);
      const nextMessage =
        mapRecordGenerationProgressMessage(payload) ||
        payload?.message ||
        message;
      const messageChanged = nextMessage !== message;
      message = nextMessage;
      lastBackendUpdateAt = Date.now();

      const step = String(payload?.step || "").toUpperCase();
      if (step === "COMPLETED" || mapped >= 100) {
        target = 100;
        if (messageChanged) emit();
        ensureTimer();
        return;
      }

      target = Math.max(target, mapped);
      if (messageChanged) emit();
      ensureTimer();
    },
    bumpTarget() {
      if (target < IDLE_BUMP_MAX) {
        target = Math.min(IDLE_BUMP_MAX, target + IDLE_BUMP_PERCENT);
        ensureTimer();
      }

      const now = Date.now();
      if (now - lastBackendUpdateAt >= STALE_EVENT_MS) {
        const waitingMessage = getWaitingMessage(now - startedAt);
        if (waitingMessage !== message) {
          message = waitingMessage;
          emit();
        }
      }
    },
    async complete(finalMessage = "Declaration of records is ready.") {
      message = finalMessage;
      target = 100;
      ensureTimer();
      await waitUntilDisplayed(100);
      emit({ step: "COMPLETED" });
    },
    stop() {
      stopTimer();
    },
  };
}
